/** @jsx jsx */
import { jsx } from '@emotion/core'
import uuid from 'uuid'
import { observer, PropTypes } from 'mobx-react'
import React, { Fragment } from 'react'
import { GAME_TYPE } from '@game-type/game-market-types'
import { MarketTrack, MatchTrack, TournamentTrack } from '@store/matchupStore'
// icon
import { TimeIco } from '@shared/Icon/MatchupIcon'
import { Win, Lose, normalBet, ResultLose, ResultWin, straightBet, ResultTxtStyle } from '@styles/match.style'
import { TxtLimit, TxtLimitPreWrap } from '@styles/base.style'
import { LS_SPORTS_TYPE_NUM, PAGE_TYPE, SPORTS_TYPE_NUM } from '@interface/common'
import {
  addNewLineToTeamName,
  getHomeOrAwayLiveScoreWithPeriodScores,
  getHomeOrAwayScoreWithPeriodScores,
  isLsportsEnv,
  isShowMatchRecordButton,
} from '@utils/utils'
import { HOME_OR_AWAY_SCORE } from '@constants/index'
import { MarketStatusType } from '@game-type/betradar-type'
import { makeVolleyballScore } from '@utils/jsxutils'
import OddsButtonGroup from './OddsButtonGroup'
import _ from 'lodash'
import classNames from 'classnames'
import homeTagMarkImg from 'assets/images/icon/matchup_icon/home-tag-mark.png'
import homeTagMarkGrayImg from 'assets/images/icon/matchup_icon/home-tag-mark-gray.png'
import { BASIC_TEAM_EMBLEM_MAP } from '@shared/EmblemGroup'
import { MatchRecordButton } from '@components/Button/MatchRecordButton'

export enum BLOCK_TYPE {
  WDL = 'wdl',
  HANDICAP = 'handicap',
  UNDEROVER = 'underover',
  LIVE = 'live',
}

export interface OddsBlockProps {
  pageType: PAGE_TYPE
  gameType: GAME_TYPE
  track: TournamentTrack
  match: MatchTrack
  marketTrack?: MarketTrack
  marketType?: string
  blockType: BLOCK_TYPE
  onFieldClickCheck?: () => boolean
  minimumLiveMatchOutcomeRate: number
}

function OddsBlock(props: OddsBlockProps) {
  const { pageType, marketTrack, match, track, blockType, onFieldClickCheck, gameType } = props
  const {
    matchStatusName,
    matchId,
    homeTeamName,
    awayTeamName,
    homeTeamEmblem,
    awayTeamEmblem,
    isOverTimeMatch,
    way,
  } = match
  const { sportId, tournamentId } = track

  let blockKey = ''
  if (blockType === BLOCK_TYPE.LIVE) {
    blockKey = matchId.toString()
  } else {
    const { id: oddsId } = marketTrack
    blockKey = oddsId
  }

  let viewKey: string
  if (marketTrack) {
    const { id: oddsId } = marketTrack
    viewKey = `${blockType}:${oddsId}`
  } else {
    viewKey = `${blockType}:${uuid.v4()}`
  }

  let isTwoWay = false
  if (pageType === PAGE_TYPE.MATCHUP_RESULT || pageType === PAGE_TYPE.MATCHUP_RESULT_FILTER) {
    if (way) {
      isTwoWay = _.size(way.outcomes) < 3
    }
  } else {
    if (marketTrack) {
      const { markets } = marketTrack
      isTwoWay = markets ? markets[0].outcomes.length < 3 : true
    }

    if (blockType === BLOCK_TYPE.LIVE) {
      if (sportId === SPORTS_TYPE_NUM.SOCCER
        || sportId === SPORTS_TYPE_NUM.ICE_HOCKEY
        || sportId === SPORTS_TYPE_NUM.HANDBALL
        || sportId === LS_SPORTS_TYPE_NUM.SOCCER
        || sportId === LS_SPORTS_TYPE_NUM.ICE_HOCKEY
        || sportId === LS_SPORTS_TYPE_NUM.HANDBALL
      ) {
        isTwoWay = false
      } else {
        isTwoWay = true
      }
    }
  }

  function makeScoreComponent() {
    if (pageType !== PAGE_TYPE.MATCHUP_RESULT
      && pageType !== PAGE_TYPE.MATCHUP_RESULT_FILTER
      && blockType !== BLOCK_TYPE.LIVE) {
      if (marketTrack != null) {
        const { markets = [] } = marketTrack
        const market = markets[0]
        const outcome = market.outcomes[0]
        if (outcome.rate === null || outcome.rate === 0) {
          return null
        }
        if (market.status !== MarketStatusType.Active) {
          return null
        }
      }
    }
    let hScore: string | number
    let aScore: string | number
    let eleHomeScore = []
    let eleAwayScore = []
    if (pageType === PAGE_TYPE.MATCHUP_RESULT || pageType === PAGE_TYPE.MATCHUP_RESULT_FILTER) {
      hScore = getHomeOrAwayScoreWithPeriodScores(HOME_OR_AWAY_SCORE.HOME, match, sportId)
      aScore = getHomeOrAwayScoreWithPeriodScores(HOME_OR_AWAY_SCORE.AWAY, match, sportId)
    } else {
      hScore = getHomeOrAwayLiveScoreWithPeriodScores(
        HOME_OR_AWAY_SCORE.HOME,
        match,
        sportId,
        blockType === BLOCK_TYPE.LIVE,
      )
      aScore = getHomeOrAwayLiveScoreWithPeriodScores(
        HOME_OR_AWAY_SCORE.AWAY,
        match,
        sportId,
        blockType === BLOCK_TYPE.LIVE,
      )

      if (sportId === SPORTS_TYPE_NUM.VOLLEYBALL
        || sportId === LS_SPORTS_TYPE_NUM.VOLLEYBALL
        || sportId === SPORTS_TYPE_NUM.TABLE_TENNIS) {
        const volleyballHomeScore = makeVolleyballScore(hScore)
        const volleyballAwayScore = makeVolleyballScore(aScore)
        hScore = volleyballHomeScore.periodScore
        eleHomeScore = volleyballHomeScore.setScore
        aScore = volleyballAwayScore.periodScore
        eleAwayScore = volleyballAwayScore.setScore
      }
    }

    let liveBoardClass = 'odds-block-list--live-board'
    if (pageType === PAGE_TYPE.MATCHUP_RESULT || pageType === PAGE_TYPE.MATCHUP_RESULT_FILTER) {
      liveBoardClass = 'odds-block-list--live-board match-result'
    }

    const centerClass = classNames('center', {
      'overtime-center': isOverTimeMatch,
    })

    const homeRate = way?.outcomes?.[0]?.rate
    const drawRate = isTwoWay ? undefined : way?.outcomes?.[1]?.rate
    const awayRate = isTwoWay ? way?.outcomes?.[1]?.rate : way?.outcomes?.[2]?.rate


    const isHomeWin = way?.outcomes?.[0]?.result ?? false
    let isHomeWinReverse = false
    if (isHomeWin) {
      if (homeRate > awayRate) {
        isHomeWinReverse = true
      }
    }

    const isAwayWin = isTwoWay ? (way?.outcomes?.[1]?.result ?? false) : (way?.outcomes?.[2]?.result ?? false)
    let isAwayWinReverse = false
    if (isAwayWin) {
      if (awayRate > homeRate) {
        isAwayWinReverse = true
      }
    }

    const isDrawWin = isTwoWay ? false : (way?.outcomes?.[1]?.result ?? false)

    // 승무패 예시종목 축구 기준으로 하였습니다
    const isDrawScore = !isTwoWay
    const oddsClass = classNames('', {
      // 정배 혹은 역배. 역배일때 붉은색으로 표시.
      'reverse': isHomeWinReverse || isAwayWinReverse,
      'is-draw-score': isDrawScore,
      'none': _.isNil(homeRate) && _.isNil(drawRate) && _.isNil(awayRate),
    })

    let txtLimitCss = TxtLimit
    let winStyle = Win
    let LoseStyle = Lose
    let homeTeamMark = <img src={homeTagMarkImg} alt="home team" className="home-tag"/>
    const teamNameMap = { 'home': homeTeamName, 'away': awayTeamName }
    if (pageType === PAGE_TYPE.MATCHUP_RESULT || pageType === PAGE_TYPE.MATCHUP_RESULT_FILTER) {
      txtLimitCss = ResultTxtStyle
      winStyle = ResultWin
      LoseStyle = ResultLose
      homeTeamMark = <img src={homeTagMarkGrayImg} alt="home team" className="home-tag"/>
      txtLimitCss = TxtLimitPreWrap
      teamNameMap.home = addNewLineToTeamName(homeTeamName)
      teamNameMap.away = addNewLineToTeamName(awayTeamName)
    }

    const homeScore = _.toNumber(hScore)
    const awayScore = _.toNumber(aScore)

    return (
      <Fragment>
        <div className={liveBoardClass}>
          {homeTeamMark}
          <span className="odds-block-list--live-board--team home" css={TxtLimit}>
            <img src={homeTeamEmblem || BASIC_TEAM_EMBLEM_MAP[sportId]} alt="home team emblem" className="emblem"/>
            <span className="name">
              <span css={txtLimitCss}>
                {teamNameMap['home']}
              </span>
            </span>
          </span>
          <div
            className={
              pageType === PAGE_TYPE.MATCHUP_RESULT
                ? 'odds-block-list--live-board--score result'
                : 'odds-block-list--live-board--score'
            }
          >
            <span className="left score-num" css={_.toNumber(hScore) > _.toNumber(aScore) ? winStyle : LoseStyle}>
              {(sportId === SPORTS_TYPE_NUM.VOLLEYBALL || sportId === LS_SPORTS_TYPE_NUM.VOLLEYBALL
                  || sportId === SPORTS_TYPE_NUM.TABLE_TENNIS) &&
                <div className="volleyball-score home">
                  {/* 스코어 만큼 갯수를 늘려주시면 됩니다. */}
                  {eleHomeScore}
                </div>
              }
              {hScore}
            </span>
            <span className={centerClass}>:</span>
            <span className="right score-num" css={_.toNumber(aScore) > _.toNumber(hScore) ? winStyle : LoseStyle}>
              {(sportId === SPORTS_TYPE_NUM.VOLLEYBALL || sportId === LS_SPORTS_TYPE_NUM.VOLLEYBALL
                  || sportId === SPORTS_TYPE_NUM.TABLE_TENNIS) &&
                <div className="volleyball-score away">
                  {eleAwayScore}
                </div>
              }
              {aScore}
            </span>
          </div>
          {(pageType === PAGE_TYPE.MATCHUP_RESULT || pageType === PAGE_TYPE.MATCHUP_RESULT_FILTER) && isOverTimeMatch &&
            <div className="overtime-wrap"><span className="overtime">연장</span></div>}
          {pageType !== PAGE_TYPE.MATCHUP_RESULT && pageType !== PAGE_TYPE.MATCHUP_RESULT_FILTER && (
            <div className="odds-block-list--live-board--status">
              <TimeIco/>
              <span className="name">{matchStatusName}</span>
            </div>
          )}
          {(pageType === PAGE_TYPE.MATCHUP_RESULT || pageType === PAGE_TYPE.MATCHUP_RESULT_FILTER) && (
            <div className="dividend-board">
              <span className={oddsClass} css={isHomeWin ? straightBet : normalBet}>
                {homeRate}
              </span>
              {isDrawScore && (
                <span className={oddsClass} css={isDrawWin ? straightBet : normalBet}>
                  {drawRate}
                </span>
              )}
              <span className={oddsClass} css={isAwayWin ? straightBet : normalBet}>
                {awayRate}
              </span>
            </div>
          )}
          <span className="odds-block-list--live-board--team away" css={TxtLimit}>
            <img src={awayTeamEmblem || BASIC_TEAM_EMBLEM_MAP[sportId]} alt="away tem emblem" className="emblem"/>
            <span className="name">
              <span css={txtLimitCss}>
                {teamNameMap['away']}
              </span>
            </span>
          </span>
          {isShowMatchRecordButton(gameType, pageType, sportId) && (
            <MatchRecordButton
              pageType={pageType}
              matchRecordSimpleMatchInfo={{
                matchId,
                sportId,
                homeScore,
                awayScore,
                homeTeamName,
                awayTeamName,
                homeTeamEmblem,
                awayTeamEmblem,
                matchStatusName,
                isLiveMatch: gameType === GAME_TYPE.LIVE,
              }}
            />
          )}
        </div>
      </Fragment>
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const makeBlockTitle = () => {
    if (blockType === BLOCK_TYPE.LIVE) {
      return ''
    }
    if (marketTrack) {
      let title = marketTrack.name

      if (pageType !== PAGE_TYPE.MATCHUP_RESULT && pageType !== PAGE_TYPE.MATCHUP_RESULT_FILTER
        && (gameType === GAME_TYPE.CROSS || gameType === GAME_TYPE.UO || gameType === GAME_TYPE.WAY)) {
        switch (marketTrack.id) {
          case '219':
            title = '승패'
            break
          case '251':
            title = '승무패'
            break
          case '223':
          case '256':
            title = '핸디캡'
            break
          case '225':
          case '258':
            title = '언더오버'
            break
        }
        if (isLsportsEnv()) {
          switch (marketTrack.id) {
            case '52':
            case '226':
              title = '승패'
              break
            case '1':
              title = '승무패'
              break
            case '3':
            case '342':
              title = '핸디캡'
              break
            case '2':
            case '28':
              title = '언더오버'
              break
          }
        }
      }

      if (title.includes('연장')) {
        const prefix = title.substr(0, title.indexOf('('))
        // const suffix = title.substr(title.indexOf('('))
        return (
          <span className="match-type">
            {prefix}
            {/*<br />*/}
            {/*<span className="small">{suffix}</span>*/}
          </span>
        )
      }
      return <span className="match-type">{marketTrack.name}</span>
    }

    if (blockType === BLOCK_TYPE.WDL && isTwoWay) {
      return <span className="match-type">승패</span>
    }
    if (blockType === BLOCK_TYPE.WDL && !isTwoWay) {
      return <span className="match-type">승무패</span>
    }
    if (blockType === BLOCK_TYPE.HANDICAP) {
      return (
        <span className="match-type">
          핸디캡
          <br/>
          <span className="small">(연장포함)</span>
        </span>
      )
    }
    if (blockType === BLOCK_TYPE.UNDEROVER) {
      return <span className="match-type">언더오버</span>
    }
    return 'Unknown'
  }
  return (
    <div key={viewKey} className={`${match.matchId}`}>
      {(blockType === BLOCK_TYPE.LIVE || pageType === PAGE_TYPE.MATCHUP_RESULT) && makeScoreComponent()}

      {pageType !== PAGE_TYPE.MATCHUP_RESULT && pageType !== PAGE_TYPE.MATCHUP_RESULT_FILTER && (
        <div className="odds-block-list--team-btn">
          {/*{makeBlockTitle()}*/}
          <OddsButtonGroup
            pageType={pageType}
            key={`${blockType}:OddsButtonGroup:${blockKey}`}
            oddsBlockType={blockType}
            match={match}
            odds={marketTrack}
            tournamentId={tournamentId}
            track={track}
            onFieldClickCheck={onFieldClickCheck}
            isTwoWay={isTwoWay}
            minimumLiveMatchOutcomeRate={props.minimumLiveMatchOutcomeRate}
          />
        </div>
      )}
    </div>
  )
}

OddsBlock.propTypes = {
  marketTrack: PropTypes.objectOrObservableObject,
}
OddsBlock.defaultProps = {
  marketTrack: null,
}

export default observer(OddsBlock)
